import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/App";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, Pill, Package, UserCog,
  LogOut, Menu, X, Shield, Building2, Eye, Globe, Settings,
  BookOpen, AlertTriangle, MessageSquare, Bell, Stethoscope, Heart,
  Moon as MoonIcon, Sun as SunIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const roleLabels = {
  super_admin: "Super Admin",
  tenant_admin: "Admin Geriátrico",
  staff_user: "Personal",
  family_user: "Familiar",
};

export default function Layout({ children }) {
  const { user, logout, impersonatedTenant, impersonate } = useAuth();
  const { darkMode, toggleDark } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [sidebarLogoSrc, setSidebarLogoSrc] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const isSuperAdmin = user?.role === "super_admin";
  const isTenantAdmin = user?.role === "tenant_admin";
  const isFamilyUser = user?.role === "family_user";

  const activeTenantId = impersonatedTenant?.id || user?.tenant_id;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isSuperAdmin) {
      api.get("/tenants").then(r => setTenants(r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const fetchNotifs = () => {
      if (!isFamilyUser) {
        api.get("/notifications/unread-count").then(r => setUnreadNotifs(r.data.unread_count)).catch(() => {});
      }
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 30000);
    return () => clearInterval(interval);
  }, [isFamilyUser, impersonatedTenant]);

  const toggleNotifications = async () => {
    if (!notifOpen) {
      try {
        const r = await api.get("/notifications");
        setNotifications(r.data);
      } catch { /* ignore */ }
    }
    setNotifOpen(!notifOpen);
  };

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setUnreadNotifs(0);
      setNotifications(n => n.map(x => ({ ...x, read: true })));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (activeTenantId) {
      setSidebarLogoSrc(`${API_URL}/api/tenants/${activeTenantId}/logo?t=${Date.now()}`);
    } else {
      setSidebarLogoSrc(`${API_URL}/api/platform/logo?t=${Date.now()}`);
    }
  }, [activeTenantId, API_URL]);

  const handleTenantSwitch = (value) => {
    if (value === "global") {
      impersonate(null, null);
    } else {
      const t = tenants.find(x => x.id === value);
      impersonate(value, t?.name || "");
    }
    navigate("/dashboard");
  };

  const hasPermission = (perm) => {
    if (user?.role === "super_admin" || user?.role === "tenant_admin") return true;
    return user?.permissions?.includes(perm);
  };

  const features = (() => {
    try { return JSON.parse(localStorage.getItem("gerion_features") || "{}"); } catch { return {}; }
  })();
  const hasFeature = (f) => isSuperAdmin || features[f] !== false;

  const superAdminGlobalView = isSuperAdmin && !impersonatedTenant;

  const navItems = [
    ...(isSuperAdmin ? [{ to: "/admin", icon: Building2, label: "Panel Global" }] : []),
    ...(!isFamilyUser && !superAdminGlobalView ? [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      ...(hasFeature("residentes") ? [{ to: "/residents", icon: Users, label: "Residentes" }] : []),
      ...(hasFeature("ronda_medicacion") ? [{ to: "/medication-round", icon: Pill, label: "Ronda Medicación" }] : []),
      ...(hasFeature("stock_medicamentos") ? [{ to: "/stock", icon: Package, label: "Stock" }] : []),
      ...(hasFeature("novedades") && hasPermission("ver_registros") ? [{ to: "/daily-logs", icon: BookOpen, label: "Novedades" }] : []),
      ...(hasFeature("incidentes") && hasPermission("ver_incidentes") ? [{ to: "/incidents", icon: AlertTriangle, label: "Incidentes" }] : []),
      ...(hasFeature("mensajes") ? [{ to: "/messages", icon: MessageSquare, label: "Mensajes" }] : []),
      ...(hasFeature("medicos") ? [{ to: "/doctors", icon: Stethoscope, label: "Médicos" }] : []),
      ...(hasFeature("portal_familiar") ? [{ to: "/portal-config", icon: Heart, label: "Portal Familiar" }] : []),
      ...(hasFeature("turnos_personal") ? [{ to: "/staff", icon: UserCog, label: "Personal" }] : []),
      ...(hasPermission("gestionar_usuarios") ? [{ to: "/users", icon: Shield, label: "Usuarios" }] : []),
      ...(isTenantAdmin || (isSuperAdmin && impersonatedTenant) ? [{ to: "/settings", icon: Settings, label: "Configuración" }] : []),
    ] : []),
    ...(isFamilyUser ? [{ to: "/family", icon: Users, label: "Mi Familiar" }] : []),
  ];

  const displayTenantName = impersonatedTenant?.name || user?.tenant_name || "Sistema Global";

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex" data-testid="main-layout">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/10 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-[272px] bg-white border-r border-slate-100 flex flex-col transition-transform duration-300 ease-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0" data-testid="sidebar-logo">
              {sidebarLogoSrc ? (
                <img
                  src={sidebarLogoSrc}
                  alt="Logo"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.onerror = null; e.target.src = "/gerion-icon.png"; }}
                />
              ) : (
                <img src="/gerion-icon.png" alt="Gerion" className="w-full h-full object-contain" />
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight font-[Manrope]">Gerion</h1>
              <p className="text-[11px] text-slate-400 font-medium truncate max-w-[160px]">{displayTenantName}</p>
            </div>
          </div>
        </div>

        {isSuperAdmin && tenants.length > 0 && (
          <div className="px-4 pb-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">Viendo como</span>
              </div>
              <Select value={impersonatedTenant?.id || "global"} onValueChange={handleTenantSwitch}>
                <SelectTrigger className="rounded-lg h-9 bg-white border-slate-200 text-sm font-medium text-slate-700" data-testid="tenant-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global"><span className="flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Vista Global</span></SelectItem>
                  {tenants.map(t => (
                    <SelectItem key={t.id} value={t.id}><span className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {t.name}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {impersonatedTenant && (
              <div className="mt-2 flex items-center justify-center">
                <Badge className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-semibold px-3 py-1">
                  <Eye className="w-3 h-3 mr-1" /> Impersonando
                </Badge>
              </div>
            )}
          </div>
        )}

        <Separator className="bg-slate-100" />

        <nav className="flex-1 p-3 space-y-0.5 overflow-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13px] font-medium transition-colors duration-150 ${
                  isActive
                    ? "bg-accent-50 text-accent-700"
                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/ó/g, 'o')}`}
            >
              <item.icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center text-violet-700 font-semibold text-sm">
              {user?.name?.charAt(0) || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-[11px] text-slate-400">{roleLabels[user?.role] || user?.role}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="rounded-full hover:bg-rose-50 hover:text-rose-500 w-8 h-8"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-100 p-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="rounded-xl lg:hidden"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="flex items-center gap-2 lg:hidden">
            <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-slate-900 text-sm">Gerion</span>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDark}
              className="rounded-full w-10 h-10 hover:bg-slate-100 bg-slate-50 border border-slate-100"
              data-testid="dark-mode-toggle"
            >
              {darkMode ? <SunIcon className="w-5 h-5 text-amber-500" /> : <MoonIcon className="w-5 h-5 text-slate-600" />}
            </Button>
            {!isFamilyUser && (
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleNotifications}
                className="rounded-full w-10 h-10 hover:bg-slate-100 relative"
                data-testid="notifications-bell"
              >
                <Bell className="w-5 h-5 text-slate-500" strokeWidth={1.8} />
                {unreadNotifs > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadNotifs > 9 ? "9+" : unreadNotifs}
                  </span>
                )}
              </Button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                  <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-lg border border-slate-100 z-50 overflow-hidden" data-testid="notifications-dropdown">
                    <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-900 text-sm">Notificaciones</h3>
                      {unreadNotifs > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={markAllRead}
                          className="text-xs text-violet-600 font-medium h-7 px-2 hover:bg-violet-50"
                          data-testid="mark-all-read-btn"
                        >
                          Marcar todas leídas
                        </Button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-6 text-sm text-slate-400 text-center">Sin notificaciones</p>
                      ) : (
                        notifications.slice(0, 10).map(n => (
                          <div
                            key={n.id}
                            className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? "bg-violet-50/50" : ""}`}
                            onClick={() => { if (n.link) navigate(n.link); setNotifOpen(false); }}
                            data-testid={`notification-${n.id}`}
                          >
                            <div className="flex items-center gap-2 mb-0.5">
                              <Badge className={`rounded-full text-[9px] font-semibold border-0 px-2 ${
                                n.type === "incidente" ? "bg-rose-50 text-rose-600" :
                                n.type === "mensaje" ? "bg-violet-50 text-violet-700" :
                                "bg-amber-50 text-amber-700"
                              }`}>
                                {n.type === "incidente" ? "Incidente" : n.type === "mensaje" ? "Mensaje" : n.type}
                              </Badge>
                              {!n.read && <span className="w-2 h-2 rounded-full bg-violet-500" />}
                            </div>
                            <p className="text-sm font-medium text-slate-900">{n.title}</p>
                            <p className="text-xs text-slate-400 mt-0.5">{n.message}</p>
                            <p className="text-[10px] text-slate-300 mt-1">{new Date(n.created_at).toLocaleString("es-AR")}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8 overflow-auto" key={impersonatedTenant?.id || "global"}>
          {children}
        </main>
      </div>
    </div>
  );
}
